/**
 * Fractions, Decimals & Percentages: equivalent/simplifying, convert F/D/P, fraction operations, percentage of amounts.
 */

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

const gcd = (a, b) => {
    let x = Math.abs(a);
    let y = Math.abs(b);
    while (y) { [x, y] = [y, x % y]; }
    return x || 1;
};

const makeDistractors = (correctStr, count = 3, isFraction = false) => {
    const seen = new Set([correctStr]);
    const out = [];
    if (isFraction) {
        const [n, d] = correctStr.split("/").map(Number);
        const offsets = [[1, 0], [0, 1], [1, 1], [-1, 0], [0, -1]];
        while (out.length < count) {
            const [on, od] = offsets[Math.floor(Math.random() * offsets.length)];
            const nn = Math.max(1, n + on);
            const dd = Math.max(1, d + od);
            const s = `${nn}/${dd}`;
            if (s !== correctStr && !seen.has(s)) { seen.add(s); out.push(s); }
        }
        return out;
    }
    const num = Number(correctStr);
    const offsets = [0.1, 0.25, 1, 2, 5, 10, -1];
    while (out.length < count) {
        const d = (num + (offsets[Math.floor(Math.random() * offsets.length)] || 1)).toFixed(2);
        const s = d.endsWith(".00") ? String(parseInt(d, 10)) : d;
        if (s !== correctStr && !seen.has(s)) { seen.add(s); out.push(s); }
    }
    return out;
};

/** Equivalent fractions, simplifying - with fraction bar visual */
export const generateEquivalentFractionsQuestion = (difficulty = "normal") => {
    let num = Math.floor(Math.random() * 9) + 4;
    let den = num + Math.floor(Math.random() * 6) + 2;
    let g = gcd(num, den);
    while (g <= 1) {
        num = Math.floor(Math.random() * 9) + 4;
        den = num + Math.floor(Math.random() * 6) + 2;
        g = gcd(num, den);
    }
    const n = num / g;
    const d = den / g;
    const answer = `${n}/${d}`;
    const q = `Simplify to lowest terms:`;
    const options = shuffle([answer, ...makeDistractors(answer, 3, true)]);
    return {
        question: q,
        fraction: { num, den },
        answer,
        options,
        type: "equivalent-fractions",
        mode: "equivalent",
        visualType: "fraction-bar",
    };
};

/** Converting between fractions, decimals and percentages - with F/D/P chart */
export const generateFractionDecimalPercentQuestion = (difficulty = "normal") => {
    const conversions = [
        { f: [1, 2], d: 0.5, p: 50 },
        { f: [1, 4], d: 0.25, p: 25 },
        { f: [3, 4], d: 0.75, p: 75 },
        { f: [1, 5], d: 0.2, p: 20 },
        { f: [2, 5], d: 0.4, p: 40 },
        { f: [1, 10], d: 0.1, p: 10 },
        { f: [1, 3], d: 0.33, p: 33 },
    ];
    const c = conversions[Math.floor(Math.random() * conversions.length)];
    const dir = Math.floor(Math.random() * 3);
    let q, answer;
    if (dir === 0) {
        q = `Convert ${c.f[0]}/${c.f[1]} to a decimal.`;
        answer = String(c.d);
    } else if (dir === 1) {
        q = `Convert ${c.f[0]}/${c.f[1]} to a percentage.`;
        answer = String(c.p);
    } else {
        q = `Convert ${c.d} to a fraction in simplest form.`;
        answer = `${c.f[0]}/${c.f[1]}`;
    }
    const opts = dir === 2
        ? makeDistractors(answer, 3, true)
        : makeDistractors(answer, 3, false);
    const options = shuffle([answer, ...opts]);
    return {
        question: q,
        fraction: { num: c.f[0], den: c.f[1] },
        decimal: c.d,
        percent: c.p,
        answer,
        options,
        type: "fraction-decimal-percent",
        mode: "convert",
        visualType: "fdp-triple",
    };
};

/** Adding, subtracting, multiplying and dividing fractions */
export const generateFractionOpsQuestion = (difficulty = "normal") => {
    const sameDenom = difficulty === "easy";
    let num1, den1, num2, den2, answerNum, answerDen, q, op;
    const opIdx = Math.floor(Math.random() * 4);
    if (sameDenom) {
        den1 = den2 = Math.floor(Math.random() * 5) + 2;
        num1 = Math.floor(Math.random() * (den1 - 1)) + 1;
        num2 = Math.floor(Math.random() * (den2 - 1)) + 1;
        if (opIdx === 0) { answerNum = num1 + num2; answerDen = den1; op = "+"; }
        else if (opIdx === 1) { answerNum = num1 - num2; answerDen = den1; op = "−"; if (answerNum < 0) { [num1, num2] = [num2, num1]; answerNum = num1 - num2; } }
        else if (opIdx === 2) { answerNum = num1 * num2; answerDen = den1 * den2; op = "×"; }
        else { answerNum = num1 * den2; answerDen = den1 * num2; op = "÷"; }
    } else {
        den1 = Math.floor(Math.random() * 4) + 2;
        den2 = Math.floor(Math.random() * 4) + 2;
        num1 = Math.floor(Math.random() * (den1 - 1)) + 1;
        num2 = Math.floor(Math.random() * (den2 - 1)) + 1;
        if (opIdx === 0) { answerNum = num1 * den2 + num2 * den1; answerDen = den1 * den2; op = "+"; }
        else if (opIdx === 1) { if (num1 * den2 - num2 * den1 < 0) { [num1, num2, den1, den2] = [num2, num1, den2, den1]; } answerNum = num1 * den2 - num2 * den1; answerDen = den1 * den2; op = "−"; }
        else if (opIdx === 2) { answerNum = num1 * num2; answerDen = den1 * den2; op = "×"; }
        else { answerNum = num1 * den2; answerDen = den1 * num2; op = "÷"; }
    }
    const g = gcd(answerNum, answerDen);
    answerNum /= g;
    answerDen /= g;
    const answer = `${answerNum}/${answerDen}`;
    q = `Calculate and give your answer in simplest form:`;
    const options = shuffle([answer, ...makeDistractors(answer, 3, true)]);
    return {
        question: q,
        expression: `${num1}/${den1} ${op} ${num2}/${den2}`,
        operands: [{ num: num1, den: den1 }, { num: num2, den: den2 }],
        operator: op,
        answer,
        options,
        type: "fraction-ops",
        mode: "fraction-ops",
        visualType: "fraction-ops",
    };
};

/** Calculating percentage of amounts (focused "X% of Y") */
// Merged into Percentage Quest (percent-quest) - use percentageQuestions.js

export const generateFractionsDecimalsPercentagesQuestion = (gameKey, difficulty = "normal") => {
    switch (gameKey) {
        case "equivalent-fractions":
            return generateEquivalentFractionsQuestion(difficulty);
        case "fraction-decimal-percent":
            return generateFractionDecimalPercentQuestion(difficulty);
        case "fraction-ops":
            return generateFractionOpsQuestion(difficulty);
        default:
            return generateEquivalentFractionsQuestion(difficulty);
    }
};
